import { Alert } from '@neo/primitives/alert'
import { Button, ButtonWithLoader } from '@neo/primitives/button'
import { ReactComponent as PlivoLogo } from '@shared/assets/illustrations/login/plivo.svg'
import { Gift } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'router-v6'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { usePermissions } from 'v2/shared/hooks/use-permissions'
import { useCompleteOnboardingMutation } from '../queries'
import { finishOnboarding } from './finish-onboarding'
import { StepBuild } from './step-build'
import { StepSource } from './step-source'
import {
  INITIAL_WIZARD_STATE,
  isComplete,
  isSourceComplete,
  voiceAgentDestination,
  type WizardState,
} from './wizard-data'

const TOTAL_STEPS = 2

// Mono eyebrow on the heading row, the same treatment as the product tags.
const StepLabel = ({ step }: { step: number }) => (
  <span
    className="shrink-0 font-jetbrains text-label uppercase text-muted-foreground"
    data-testid="onboarding-step-label"
  >
    Step {step} of {TOTAL_STEPS}
  </span>
)

// Two steps: the build question (with its inline framework probe), then the
// source question. Both required; only the second posts.
//
// The legacy business-type question that used to come first was dropped. Its
// answer survives only as finishOnboarding's `businessType` argument, passed as
// '' — core treats business_type as optional and the payload builder omits an
// empty string, so nothing downstream had to change.
export const OnboardingWizard = () => {
  const navigate = useNavigate()
  const { data: profile, isLoading: isProfileLoading } = useProfileQuery()
  const { permissions } = usePermissions()
  const featureFlags = useFeatureFlags()
  const { mutate: completeOnboarding, isPending } = useCompleteOnboardingMutation()
  const [state, setState] = useState<WizardState>(INITIAL_WIZARD_STATE)
  const [step, setStep] = useState(1)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const mounted = useRef(false)

  // Continue and Back unmount on click, which drops focus to <body>. Focusing the
  // new step's heading announces it and scrolls it into view. Skipped on first
  // render so the page does not steal focus on load.
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
      return
    }
    headingRef.current?.focus()
  }, [step])

  // Functional update — same-tick patches can't drop each other. Answers are
  // persisted once, on successful finish (finish-onboarding.ts): every reader
  // lives on a post-finish page, so half-typed abandoned answers never need
  // to reach localStorage.
  const update = (patch: Partial<WizardState>) => setState((prev) => ({ ...prev, ...patch }))

  // Clamped: a second click on an exiting step must be a no-op, not an empty panel.
  const next = () => setStep((s) => Math.min(TOTAL_STEPS, s + 1))
  const back = () => setStep((s) => Math.max(1, s - 1))

  // Success navigates (replacing /onboarding in history); failure keeps the
  // user here with answers intact — see finish-onboarding.ts.
  const finish = () =>
    finishOnboarding({
      state,
      businessType: '',
      org: profile?.organizations?.[0],
      voiceAgentPath: voiceAgentDestination({
        permissionsMap: permissions,
        profileData: profile,
        featureFlags,
      }),
      completeOnboarding,
      navigate,
    })

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      {/* Logo pinned: it sits OUTSIDE the scroller below, so long step content
          scrolls under it rather than carrying it off-screen. */}
      <div className="w-full shrink-0 px-6 pt-6">
        <PlivoLogo aria-label="Plivo" className="h-6 w-auto" />
      </div>

      {/* The scroller is FULL-WIDTH with the content column centred inside it,
          not the centred column itself — a scroll container capped at max-w-2xl
          puts its scrollbar at the column's edge, mid-screen. This way it sits
          at the viewport's right edge. min-h-0 on both: flex children refuse to
          shrink below content otherwise, and the overflow never engages. */}
      <div className="min-h-0 w-full flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-5 px-6 pb-8 pt-6">
          {/* A note, not a status: the info wash without the hairline. */}
          <Alert
            variant="info"
            icon={<Gift size={16} />}
            className="mb-6 border-0 py-2"
            content={
              <>
                Help us with a few quick questions, and we'll get your account ready with{' '}
                <span className="text-s-600">Free Credits</span>
              </>
            }
          />

          {step === 1 ? (
            <>
              <StepBuild
                state={state}
                update={update}
                stepLabel={<StepLabel step={1} />}
                headingRef={headingRef}
              />
              <div className="flex items-center justify-end">
                <Button
                  onClick={next}
                  disabled={!isComplete(state)}
                  data-testid="onboarding-continue-btn"
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between gap-3">
                  <h1
                    ref={headingRef}
                    tabIndex={-1}
                    className="text-h3-500 text-foreground outline-none"
                  >
                    How did you hear about Plivo?
                  </h1>
                  <StepLabel step={2} />
                </div>
                <StepSource state={state} update={update} />
              </div>
              <div className="flex items-center justify-end gap-3">
                <Button variant="outline" onClick={back} data-testid="onboarding-back-btn">
                  Back
                </Button>
                <ButtonWithLoader
                  onClick={finish}
                  // Also gated on the profile: submitting before it resolves would
                  // navigate away without ever posting, and the post-signup
                  // redirect would bounce the user back into the wizard.
                  disabled={!isComplete(state) || !isSourceComplete(state) || isProfileLoading}
                  loading={isPending}
                  data-testid="onboarding-get-started-btn"
                >
                  Get started
                </ButtonWithLoader>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
